import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { ToastContext, type ToastContextValue, type ToastType } from './toastKontext';
import './Toast.css';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={16} />,
  error:   <XCircle size={16} />,
  warning: <AlertTriangle size={16} />,
  info:    <Info size={16} />,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: string) =>
    setToasts(t => t.filter(x => x.id !== id)), []);

  const add = useCallback((type: ToastType, message: string, duration = 3500) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, type, message, duration }]);
    setTimeout(() => remove(id), duration);
  }, [remove]);

  const ctx: ToastContextValue = {
    success: (m) => add('success', m),
    error:   (m) => add('error', m, 5000),
    warning: (m) => add('warning', m, 4000),
    info:    (m) => add('info', m),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // mount → animate in
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`toast toast-${toast.type} ${visible ? 'toast-in' : ''}`}
      role={toast.type === 'error' ? 'alert' : undefined}
    >
      <span className="toast-icon" aria-hidden="true">{ICONS[toast.type]}</span>
      <span className="toast-message">{toast.message}</span>
      <button type="button" className="toast-close" onClick={() => onRemove(toast.id)} aria-label="Meldung schließen">
        <X size={14} />
      </button>
    </div>
  );
}
